/**
 * PinterestPublishCard — section "Publication Pinterest" sur
 * `<WorkPublicationsTab>` (S7-PR2).
 *
 * Distinct du `<PlatformPublishCard>` Etsy car Pinterest a son propre
 * shape de publish :
 *   - Pas de mockup template picker (Pinterest réutilise les mockups Etsy
 *     existants en les recropant 2:3 runtime).
 *   - Board picker REQUIS (sélection user d'un board synchronisé).
 *   - Strategy toggle REQUIS (Carrousel atomique vs N pins separate —
 *     décision UX user-controlled selon stratégie de reach).
 *
 * Flow :
 *   1. Lit les boards Pinterest synchronisés (filtre is_owner=true).
 *   2. Affiche un `<Select>` board + un `<ChipMultiSelect>`-like toggle
 *      stratégie + sélecteur variant (auto-pick 1ère active sans Pin
 *      existant).
 *   3. Bouton "Publier sur Pinterest" :
 *      - Si board.privacy='secret' → confirm modal "Pin invisible
 *        publiquement".
 *      - POST /api/works/{id}/publications/pinterest.
 *      - Si réponse `blocked=true` → afficher card "Décision requise"
 *        avec 2 boutons (Publier en mono / Annuler).
 *      - Sinon → toast info "Publication mise en file".
 *   4. Affiche les publications Pinterest existantes (lecture
 *      `useWorkPublications(platform=pinterest)`).
 *
 * Pas de polling FE — le journal est rafraîchi à la demande via le
 * `<WorkPublicationsTab>` parent qui invalide ses queries.
 *
 * Verrous tenus :
 *   - Aucun `<button>` natif (utilise `<Button>`).
 *   - Aucun `window.confirm` (utilise `useConfirm`).
 *   - Pas de hex direct (tokens DS via classes).
 */

import { useMemo, useState } from 'react'
import { ExternalLink, Loader2, Send } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Select from '@/components/ui/Select'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useDrafts } from '@/hooks/useDrafts'
import {
  useCancelPinterestBlocked,
  usePinterestBoards,
  usePlatformConnections,
  usePublishToPinterest,
  useRetryPinterestBlockedMono,
} from '@/hooks/usePlatformConnections'
import { useWorkPublications } from '@/hooks/useWorkPublications'
import type { PinterestStrategy } from '@/types/platform-connections'
import type { PlatformResourceResponse } from '@/types/publications'
import type { WorkVariantWithCatalogResponse } from '@/types/works'

import './PinterestPublishCard.css'

interface PinterestPublishCardProps {
  workId: number
  workTitle: string
  variants: WorkVariantWithCatalogResponse[]
}

const STRATEGY_OPTIONS: ReadonlyArray<{
  value: PinterestStrategy
  label: string
  hint: string
}> = [
  {
    value: 'carousel',
    label: 'Carrousel unique',
    hint: '1 Pin contenant toutes les images (swipe horizontal)',
  },
  {
    value: 'separate',
    label: 'Pins séparés',
    hint: '1 Pin par image (descriptions différenciées)',
  },
]

export default function PinterestPublishCard({
  workId,
  workTitle,
  variants,
}: PinterestPublishCardProps) {
  const { confirm } = useConfirm()
  const connectionsQuery = usePlatformConnections('pinterest')
  const connection = (connectionsQuery.data ?? []).find((c) => !c.revoked_at) ?? null

  const boardsQuery = usePinterestBoards(connection?.id ?? null, {
    enabled: connection !== null,
  })
  const boards = boardsQuery.data ?? []

  const draftsQuery = useDrafts(workId)
  const pinterestDraft = useMemo(
    () =>
      (draftsQuery.data ?? []).find((d) => d.kind === 'pinterest_pin' && d.status === 'approved') ??
      null,
    [draftsQuery.data],
  )

  const publicationsQuery = useWorkPublications({
    workId,
    platform: 'pinterest' as const,
  })
  // `publications` est wrap dans useMemo pour stabiliser la référence —
  // sans ça, `[].filter(...)` créerait un nouveau tableau à chaque render
  // et invaliderait inutilement les useMemo dépendants (variantWithoutPin).
  const publications = useMemo(() => publicationsQuery.data ?? [], [publicationsQuery.data])
  const blockedPublications = useMemo(
    () => publications.filter((p) => p.state === 'blocked'),
    [publications],
  )

  // Auto-pick : 1ère variante active sans Pin Pinterest publié.
  const activeVariants = useMemo(() => variants.filter((v) => v.status === 'active'), [variants])
  const variantWithoutPin = useMemo(() => {
    const variantsWithPin = new Set(
      publications.filter((p) => p.state === 'published').map((p) => p.work_variant_id),
    )
    return activeVariants.find((v) => !variantsWithPin.has(v.id)) ?? null
  }, [activeVariants, publications])

  const [selectedBoardId, setSelectedBoardId] = useState<string>('')
  const [selectedStrategy, setSelectedStrategy] = useState<PinterestStrategy>('carousel')
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    variantWithoutPin?.id ?? null,
  )

  const publishMutation = usePublishToPinterest()
  const retryMonoMutation = useRetryPinterestBlockedMono()
  const cancelMutation = useCancelPinterestBlocked()

  const isLoading =
    connectionsQuery.isLoading ||
    boardsQuery.isLoading ||
    draftsQuery.isLoading ||
    publicationsQuery.isLoading

  const handlePublish = async () => {
    if (!connection) {
      toast.error('Aucune connexion Pinterest active.')
      return
    }
    if (!pinterestDraft || pinterestDraft.current_version_id == null) {
      toast.error(
        'Aucun draft Pinterest approuvé pour cette œuvre — approuve un draft puis réessaye.',
      )
      return
    }
    if (!selectedBoardId) {
      toast.error('Choisis un board avant de publier.')
      return
    }
    if (selectedVariantId == null) {
      toast.error('Choisis une variante à publier.')
      return
    }
    const board = boards.find((b) => b.external_board_id === selectedBoardId)

    // Confirm modal pour les boards secret (T9 plan). On utilise variant
    // 'default' (la palette `info` n'existe pas dans le DialogVariant DS).
    if (board && board.privacy === 'secret') {
      const ok = await confirm({
        title: 'Board privé (Secret)',
        message: `Le board « ${board.name} » est privé sur Pinterest. Le Pin ne sera visible que par toi sur Pinterest, pas par le public. Continuer ?`,
        variant: 'default',
        confirmText: 'Publier quand même',
      })
      if (!ok) return
    }

    try {
      const result = await publishMutation.mutateAsync({
        workId,
        payload: {
          variant_id: selectedVariantId,
          draft_id: pinterestDraft.id,
          board_id: selectedBoardId,
          pinterest_strategy: selectedStrategy,
          link: null,
        },
      })
      if (result.blocked) {
        toast.info(
          'Publication bloquée : seul le visuel plat est disponible — décide entre Publier en mono-image ou Annuler ci-dessous.',
        )
      } else {
        toast.success(
          `Publication ${selectedStrategy === 'carousel' ? 'en carrousel' : `en ${result.intent_count} Pins séparés`} mise en file.`,
        )
      }
      void publicationsQuery.refetch()
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Publication Pinterest impossible.'
      toast.error(detail)
    }
  }

  const handleRetryMono = async (resourceId: number) => {
    const ok = await confirm({
      title: 'Publier en mono-image quand même ?',
      message:
        'Le Pin sera créé avec uniquement le visuel plat (pas de carrousel). Tu peux republier plus tard avec d’autres mockups si tu en ajoutes côté Etsy.',
      variant: 'default',
      confirmText: 'Publier en mono',
    })
    if (!ok) return
    try {
      const result = await retryMonoMutation.mutateAsync({ workId, resourceId })
      if (result.blocked) {
        toast.error('Toujours bloqué — état inattendu, contacte le support.')
      } else {
        toast.success('Publication mono-image mise en file.')
      }
      void publicationsQuery.refetch()
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Action impossible.'
      toast.error(detail)
    }
  }

  const handleCancelBlocked = async (resourceId: number) => {
    const ok = await confirm({
      title: 'Annuler la publication ?',
      message:
        'La publication est marquée comme annulée. L’historique reste consultable dans le journal. Tu peux republier l’œuvre à tout moment.',
      variant: 'danger',
      confirmText: 'Annuler la publication',
    })
    if (!ok) return
    try {
      await cancelMutation.mutateAsync({ workId, resourceId })
      toast.success('Publication annulée.')
      void publicationsQuery.refetch()
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Action impossible.'
      toast.error(detail)
    }
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <article className="pinterest-publish-card" aria-label="Publication Pinterest">
      <header className="pinterest-publish-card-header">
        <div>
          <h3 className="ui-h4 pinterest-publish-card-title">Publication Pinterest</h3>
          <p className="ui-body ui-text-muted pinterest-publish-card-subtitle">{workTitle}</p>
        </div>
        {connection ? (
          <Badge variant="success" size="sm" dot>
            Compte connecté
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            Pas connecté
          </Badge>
        )}
      </header>

      {!connection ? (
        <p className="ui-body ui-text-muted">
          Connecte un compte Pinterest depuis <strong>Réglages → Comptes connectés</strong> pour
          publier.
        </p>
      ) : isLoading ? (
        <p className="ui-body ui-text-muted" role="status">
          Chargement…
        </p>
      ) : (
        <PinterestPublishForm
          boards={boards}
          activeVariants={activeVariants}
          hasDraft={pinterestDraft !== null}
          selectedBoardId={selectedBoardId}
          selectedStrategy={selectedStrategy}
          selectedVariantId={selectedVariantId}
          onBoardChange={setSelectedBoardId}
          onStrategyChange={setSelectedStrategy}
          onVariantChange={setSelectedVariantId}
          onPublish={handlePublish}
          publishPending={publishMutation.isPending}
        />
      )}

      {/* Décisions blocked en attente — affichées juste sous le formulaire. */}
      {blockedPublications.length > 0 ? (
        <ul className="pinterest-publish-card-blocked-list" aria-label="Décisions requises">
          {blockedPublications.map((pub) => (
            <BlockedDecisionRow
              key={pub.id}
              onRetryMono={() => handleRetryMono(pub.id)}
              onCancel={() => handleCancelBlocked(pub.id)}
              retryPending={retryMonoMutation.isPending}
              cancelPending={cancelMutation.isPending}
            />
          ))}
        </ul>
      ) : null}

      {/* Liste des Pins publiés. Le journal complet vit dans le tab parent ;
          ici on liste juste les Pins Pinterest pour donner un feedback rapide. */}
      {publications.length > 0 ? (
        <ul className="pinterest-publish-card-pins-list" aria-label="Pins publiés">
          {publications
            .filter((p) => p.state === 'published')
            .map((pub) => (
              <PublishedPinRow key={pub.id} publication={pub} />
            ))}
        </ul>
      ) : null}
    </article>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Sous-composant — Formulaire publish
// ──────────────────────────────────────────────────────────────────────────────

interface PinterestPublishFormProps {
  boards: ReturnType<typeof usePinterestBoards>['data'] extends infer T
    ? T extends Array<infer U>
      ? U[]
      : never
    : never
  activeVariants: WorkVariantWithCatalogResponse[]
  hasDraft: boolean
  selectedBoardId: string
  selectedStrategy: PinterestStrategy
  selectedVariantId: number | null
  onBoardChange: (boardId: string) => void
  onStrategyChange: (strategy: PinterestStrategy) => void
  onVariantChange: (variantId: number) => void
  onPublish: () => void
  publishPending: boolean
}

function PinterestPublishForm({
  boards,
  activeVariants,
  hasDraft,
  selectedBoardId,
  selectedStrategy,
  selectedVariantId,
  onBoardChange,
  onStrategyChange,
  onVariantChange,
  onPublish,
  publishPending,
}: PinterestPublishFormProps) {
  if (boards.length === 0) {
    return (
      <p className="ui-body ui-text-muted">
        Aucun board synchronisé. Va sur <strong>Réglages → Comptes connectés</strong> et clique «
        Rafraîchir mes boards » sur la carte Pinterest.
      </p>
    )
  }

  const canPublish =
    hasDraft && selectedBoardId !== '' && selectedVariantId !== null && !publishPending

  return (
    <div className="pinterest-publish-card-form">
      <Field label="Board cible">
        {(controlProps) => (
          <Select
            {...controlProps}
            value={selectedBoardId}
            onChange={(e) => onBoardChange(e.target.value)}
            placeholder="— Choisir un board —"
          >
            {boards.map((b) => (
              <option key={b.external_board_id} value={b.external_board_id}>
                {b.name}
                {b.privacy === 'secret' ? ' · Secret' : ''}
                {b.privacy === 'protected' ? ' · Protégé' : ''}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Variante à publier">
        {(controlProps) => (
          <Select
            {...controlProps}
            value={selectedVariantId ?? ''}
            onChange={(e) => onVariantChange(Number(e.target.value))}
            placeholder="— Choisir une variante —"
          >
            {activeVariants.map((v) => {
              // Refonte POD v6.1 : `format` singulier (`v.formats[]` retiré).
              const formatLabel = v.format?.label_fr ?? `Variante #${v.id}`
              const framingLabel = v.framing_label_fr ?? null
              return (
                <option key={v.id} value={v.id}>
                  {formatLabel}
                  {framingLabel ? ` · ${framingLabel}` : ''}
                </option>
              )
            })}
          </Select>
        )}
      </Field>

      <div
        className="pinterest-publish-card-strategy"
        role="radiogroup"
        aria-label="Stratégie de publication"
      >
        <p className="ui-eyebrow pinterest-publish-card-strategy-legend">
          Stratégie de publication
        </p>
        {STRATEGY_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onStrategyChange(opt.value)}
            role="radio"
            aria-checked={selectedStrategy === opt.value}
            data-selected={selectedStrategy === opt.value || undefined}
            className="pinterest-publish-card-strategy-option"
          >
            <span className="pinterest-publish-card-strategy-label">{opt.label}</span>
            <span className="pinterest-publish-card-strategy-hint">{opt.hint}</span>
          </Button>
        ))}
      </div>

      {!hasDraft ? (
        <p className="ui-body ui-text-muted">
          Aucun draft Pinterest approuvé pour cette œuvre — va sur <strong>Contenu généré</strong>{' '}
          pour générer / approuver un draft Pinterest.
        </p>
      ) : null}

      <div className="pinterest-publish-card-actions">
        <Button variant="primary" size="md" onClick={onPublish} disabled={!canPublish}>
          {publishPending ? (
            <Loader2 aria-hidden="true" size={16} className="platform-spinner" />
          ) : (
            <Send aria-hidden="true" size={16} />
          )}
          Publier sur Pinterest
        </Button>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Sous-composant — Décision blocked en attente
// ──────────────────────────────────────────────────────────────────────────────

interface BlockedDecisionRowProps {
  onRetryMono: () => void
  onCancel: () => void
  retryPending: boolean
  cancelPending: boolean
}

function BlockedDecisionRow({
  onRetryMono,
  onCancel,
  retryPending,
  cancelPending,
}: BlockedDecisionRowProps) {
  return (
    <li className="pinterest-publish-card-blocked-row">
      <div>
        <p className="ui-body pinterest-publish-card-blocked-title">
          <strong>Décision requise</strong> — publication carrousel bloquée
        </p>
        <p className="ui-body ui-text-muted pinterest-publish-card-blocked-explainer">
          La cascade n&apos;a résolu que le visuel plat (aucun mockup utilisable pour cette
          variante). Choisis :
        </p>
      </div>
      <div className="pinterest-publish-card-blocked-actions">
        <Button
          variant="primary"
          size="sm"
          onClick={onRetryMono}
          disabled={retryPending || cancelPending}
        >
          {retryPending ? (
            <Loader2 aria-hidden="true" size={14} className="platform-spinner" />
          ) : null}
          Publier en mono-image
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={retryPending || cancelPending}
        >
          {cancelPending ? (
            <Loader2 aria-hidden="true" size={14} className="platform-spinner" />
          ) : null}
          Annuler
        </Button>
      </div>
    </li>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Sous-composant — Pin publié
// ──────────────────────────────────────────────────────────────────────────────

interface PublishedPinRowProps {
  publication: PlatformResourceResponse
}

function PublishedPinRow({ publication }: PublishedPinRowProps) {
  const publishedDate = publication.published_at
    ? new Date(publication.published_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—'
  return (
    <li className="pinterest-publish-card-pin-row">
      <span className="ui-body">
        Pin publié le <strong>{publishedDate}</strong>
      </span>
      {publication.url ? (
        <a
          href={publication.url}
          target="_blank"
          rel="noopener noreferrer"
          className="ui-link pinterest-publish-card-pin-link"
        >
          Voir sur Pinterest
          <ExternalLink aria-hidden="true" size={14} />
        </a>
      ) : null}
    </li>
  )
}
